'use client'
import { memo, useEffect, useState } from 'react'

function RatingsBarBase({ reviews = [] }) {
  // Process reviews data to get rating counts
  interface Review {
    rating: number
    // Add other fields if needed
  }

  interface RatingData {
    star: number
    count: number
    percentage: number
  }

  const processReviewData = (reviewsData: Review[]): RatingData[] => {
    // Count ratings by star (rounded to nearest integer)
    const ratingCounts: Record<1 | 2 | 3 | 4 | 5, number> = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    }

    reviewsData.forEach((review: Review) => {
      const roundedRating = Math.round(review.rating)
      if (roundedRating >= 1 && roundedRating <= 5) {
        ratingCounts[roundedRating as 1 | 2 | 3 | 4 | 5]++
      }
    })

    const maxCount = Math.max(...Object.values(ratingCounts))

    // Create ratings array with calculated percentages
    return [5, 4, 3, 2, 1].map(
      (star): RatingData => ({
        star,
        count: ratingCounts[star as keyof typeof ratingCounts],
        percentage:
          maxCount > 0
            ? ratingCounts[star as keyof typeof ratingCounts] / maxCount
            : 0,
      }),
    )
  }

  const ratings = processReviewData(reviews)

  const [animateProgress, setAnimateProgress] = useState(
    Array(ratings.length).fill(0),
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateProgress(ratings.map((r) => r.percentage * 100))
    }, 100)
    return () => clearTimeout(timer)
  }, [ratings])

  return (
    <div className='md:w-[325px] w-[300px] space-y-[5.03px] p-1 md:p-0'>
      {ratings?.map((r, index) => (
        <div
          key={r.star}
          className='flex items-center gap-4'
        >
          {/* Star number */}
          <span className='w-[15.32px] h-[24.08px] font-poppins font-medium text-[16.57px] leading-[150%] tracking-[1%] align-middle text-[#1E1F24]'>
            {r.star}
          </span>

          {/* Progress bar background */}
          <div className='flex-1 h-[7.98px] rounded-full bg-gray-200 overflow-hidden relative'>
            {/* Filled portion */}
            <div
              className='h-full bg-gray-800 rounded-full transition-transform duration-500 ease-in-out w-full'
              style={{
                transform: `translateX(${animateProgress[index] - 100}%)`,
              }}
              role='progressbar'
              aria-valuenow={animateProgress[index]}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>

          {/* Count — always visible */}
          <span className='min-w-[24px] w-[20.79px] h-[24.08px] font-normal text-[13px] leading-[187%] tracking-[0] text-[#191919]'>
            {r.count}
          </span>
        </div>
      ))}
    </div>
  )
}

// Memoize the component so it only re-renders when `reviews` prop identity or contents (shallow) change
const RatingsBar = memo(RatingsBarBase)

export default RatingsBar
